import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Server, Shield, Code, Settings, BarChart, Network, Database, ArrowRight, CheckCircle, Monitor } from 'lucide-react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import PageBanner from '../components/Banner';

const ACCENT = '#E8611A';

const ConsultingService = ({ title, description, features, icon: Icon, accent }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff', borderRadius: 16, padding: '28px 28px 24px',
        border: hovered ? `1.5px solid ${accent}40` : '1.5px solid rgba(0,0,0,0.07)',
        boxShadow: hovered ? `0 12px 36px rgba(0,0,0,0.10)` : '0 2px 10px rgba(0,0,0,0.05)',
        transition: 'box-shadow .25s, border-color .25s, transform .25s',
        transform: hovered ? 'translateY(-4px)' : 'none',
        fontFamily: "'DM Sans',sans-serif",
        height: '100%', boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: `${accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .25s', ...(hovered ? { background: accent } : {}) }}>
          <Icon size={22} color={hovered ? '#fff' : accent} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: '0 0 6px' }}>{title}</h3>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 14px', lineHeight: 1.6 }}>{description}</p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
            {features.map((f, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', color: '#475569' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const EtudesConseilInfo = () => {
  const navigate = useNavigate();
  const consultingServices = [
    {
      icon: Settings,
      title: "Conseil en Architecture Informatique",
      description: "Conception et optimisation de votre infrastructure IT",
      features: [
        "Analyse de l'existant et recommandations",
        "Conception d'architectures évolutives",
        "Optimisation des performances",
        "Plans de migration et d'évolution"
      ]
    },
    {
      icon: Shield,
      title: "Audit et Sécurité",
      description: "Évaluation et renforcement de la sécurité de vos systèmes",
      features: [
        "Audit de sécurité complet",
        "Tests de pénétration",
        "Plan de sécurisation",
        "Mise en conformité RGPD"
      ]
    },
    {
      icon: Database,
      title: "Gestion des Données",
      description: "Optimisation de vos systèmes de gestion de données",
      features: [
        "Architecture des bases de données",
        "Plan de sauvegarde et restauration",
        "Optimisation des performances",
        "Migration des données"
      ]
    },
    {
      icon: Network,
      title: "Infrastructure Réseau",
      description: "Conception et optimisation de votre réseau d'entreprise",
      features: [
        "Audit de l'infrastructure existante",
        "Conception de solutions réseau",
        "Sécurisation des accès",
        "Plan de maintenance"
      ]
    }
  ];

  const expertiseAreas = [
    {
      icon: Monitor,
      title: "Systèmes d'Information",
      items: [
        "Architecture SI",
        "Intégration de solutions",
        "Transformation digitale",
        "Optimisation des processus"
      ]
    },
    {
      icon: Server,
      title: "Infrastructure",
      items: [
        "Cloud Computing",
        "Virtualisation",
        "Stockage",
        "Sauvegarde"
      ]
    },
    {
      icon: Code,
      title: "Développement",
      items: [
        "Applications sur mesure",
        "Intégration API",
        "Microservices",
        "DevOps"
      ]
    },
    {
      icon: BarChart,
      title: "Performance & Analyse",
      items: [
        "Monitoring",
        "Optimisation",
        "Reporting",
        "Analyse prédictive"
      ]
    }
  ];

  const CARD_ACCENTS = [ACCENT, '#3b82f6', '#16a34a', '#7c3aed'];

  return (
    <Layout>
      <Helmet>
        <title>Études et Conseils Informatique - Aydees | Expertise IT au Niger</title>
        <meta name="description" content="Services d'études et conseils en informatique par Aydees. Architecture IT, audit sécurité, transformation digitale et accompagnement technique au Niger." />
        <meta name="keywords" content="conseil informatique, étude IT, architecture système, audit sécurité, Aydees, Niger, transformation digitale" />
      </Helmet>

      <PageBanner
        title="Études & Conseils Informatiques"
        subtitle="Expertise et accompagnement pour vos projets informatiques"
      />

      {/* Approche — étapes */}
      <section style={{ background: 'linear-gradient(135deg,#fff7ed 0%,#ffffff 60%,#f8fafc 100%)', padding: '64px 24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.35, pointerEvents: 'none', backgroundImage: 'radial-gradient(circle,#e2e8f0 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 16px', borderRadius: 999, border: `1px solid ${ACCENT}30`, background: `${ACCENT}0d`, marginBottom: 12 }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: ACCENT, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Notre approche</span>
            </div>
            <h2 style={{ fontSize: 'clamp(1.4rem,2.5vw,1.9rem)', fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>Une méthodologie éprouvée</h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', maxWidth: 500, margin: '0 auto' }}>Pour répondre précisément à vos besoins informatiques</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 20 }}>
            {[
              { step: '01', title: 'Analyse', desc: "Étude approfondie de vos besoins et de votre environnement informatique" },
              { step: '02', title: 'Diagnostic', desc: "Identification des points d'amélioration et des solutions potentielles" },
              { step: '03', title: 'Recommandations', desc: "Proposition de solutions adaptées et plan d'action détaillé" },
              { step: '04', title: 'Accompagnement', desc: "Suivi de la mise en œuvre et support continu" },
            ].map((p, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 16, padding: '24px 20px', border: '1.5px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', fontFamily: "'DM Sans',sans-serif" }}>
                <div style={{ fontSize: '2rem', fontWeight: 900, color: `${ACCENT}30`, marginBottom: 10, lineHeight: 1 }}>{p.step}</div>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', margin: '0 0 6px' }}>{p.title}</h3>
                <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0, lineHeight: 1.6 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services de conseil */}
      <section style={{ background: '#f8fafc', padding: '64px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: 'clamp(1.4rem,2.5vw,1.9rem)', fontWeight: 800, color: '#1e293b', margin: '0 0 8px', fontFamily: "'DM Sans',sans-serif" }}>Services de Conseil</h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', maxWidth: 500, margin: '0 auto', fontFamily: "'DM Sans',sans-serif" }}>Des solutions expertes pour votre transformation numérique</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 20, alignItems: 'stretch' }}>
            {consultingServices.map((service, i) => (
              <ConsultingService key={i} {...service} accent={CARD_ACCENTS[i % CARD_ACCENTS.length]} />
            ))}
          </div>
        </div>
      </section>

      {/* Domaines d'expertise */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: 'clamp(1.4rem,2.5vw,1.9rem)', fontWeight: 800, color: '#1e293b', margin: '0 0 8px', fontFamily: "'DM Sans',sans-serif" }}>Nos Domaines d'Expertise</h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', maxWidth: 500, margin: '0 auto', fontFamily: "'DM Sans',sans-serif" }}>Une expertise technique pointue au service de vos projets</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 20 }}>
            {expertiseAreas.map((area, i) => {
              const ac = CARD_ACCENTS[i % CARD_ACCENTS.length];
              return (
                <div key={i} style={{ background: '#fff', borderRadius: 16, padding: '24px 20px', border: '1.5px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', fontFamily: "'DM Sans',sans-serif" }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: `${ac}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <area.icon size={20} color={ac} />
                  </div>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>{area.title}</h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {area.items.map((item, idx) => (
                      <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', color: '#475569' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: ac, flexShrink: 0 }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)', padding: '64px 24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: `${ACCENT}12`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '-20%', right: '5%', width: 340, height: 340, borderRadius: '50%', background: `radial-gradient(circle,${ACCENT}20 0%,transparent 70%)`, filter: 'blur(60px)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1, fontFamily: "'DM Sans',sans-serif" }}>
          <h2 style={{ fontSize: 'clamp(1.4rem,2.5vw,2rem)', fontWeight: 800, color: '#fff', margin: '0 0 12px' }}>Besoin d'un conseil d'expert ?</h2>
          <p style={{ color: '#94a3b8', fontSize: '0.95rem', margin: '0 0 28px', lineHeight: 1.7 }}>Nos consultants sont à votre disposition pour étudier vos besoins et proposer les meilleures solutions.</p>
          <button
            onClick={() => navigate('/rejoignez-nous')}
            onMouseEnter={e => { e.currentTarget.style.background = '#d4501a'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = ACCENT; e.currentTarget.style.transform = 'none'; }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 28px', borderRadius: 10, background: ACCENT, color: '#fff', fontWeight: 700, fontSize: '0.9rem', border: 'none', cursor: 'pointer', transition: 'background .2s, transform .2s', boxShadow: `0 6px 20px ${ACCENT}40` }}
          >
            Prendre rendez-vous <ArrowRight size={17} />
          </button>
        </div>
      </section>
    </Layout>
  );
};

export default EtudesConseilInfo;